<template>
	<main v-editable="blok" class="relative overflow-hidden" aria-label="Hlavní obsah">
		<h1 class="sr-only">
			{{ GLOBUS_SEO_TITLE }}
		</h1>
		<InformationBar v-if="informationBar" :blok="informationBar" />
		<StoryblokComponent
			v-for="item in blok.intro"
			:key="item._uid"
			:blok="item"
		/>
		<div class="space-y-section">
			<HypermarketSelectionBanner
				class="container"
				show-links
				redirect-to-branch
				dropdown-default-hm
			>
				<template #header>
					<h3 class="typo-h2">
						Detail pobočky a otevírací doba
					</h3>
				</template>
			</HypermarketSelectionBanner>

			<BranchProductListing />

			<StoryblokComponent
				v-if="campaignBanner"
				:id="campaignBanner.slug"
				:blok="campaignBanner.content"
			/>

			<LeafletListing />

			<Click
				class="container"
				:placement="AdPosition.HOMEPAGE_1"
			/>

			<StoryblokComponent
				v-if="newsletterBanner"
				:id="newsletterBanner.slug"
				:blok="newsletterBanner.content"
			/>

			<StoryblokComponent
				v-for="item in blok.carousel"
				:key="item._uid"
				:blok="item"
				class="!bg-white !pt-0 !pb-0"
			/>

			<HomepageHMOverview
				:restaurant-promotion-tile-desktop="blok.promotionTileDesktop"
				:restaurant-promotion-tile-mobile="blok.promotionTileMobile"
			/>

			<Click :placement="AdPosition.HOMEPAGE_2" class="container" />

			<section class="container" aria-label="Inspirujte se">
				<h2 class="typo-h1 mb-6 md:mb-10 text-heading-primary">
					Inspirujte se
				</h2>
				<nav class="overflow-x-auto mb-6 md:mb-8" aria-label="Záložky pro sekci inspirujte se">
					<HorizontalScroll
						class="px-4 md:px-0 flex gap-2"
						role="tablist"
						fade-edges
					>
						<li v-for="item in inspirationsTabs" :key="item.id" role="tab" class="inline-flex">
							<UiTabToggle
								:id="slugify(`inspirujte-se-${item.label}`, {lower: true})"
								:as="AbsoluteInternalLink"
								:to="{hash: '#' + slugify(`inspirujte-se-${item.label}`, {lower: true})}"
								:aria-controls="slugify(`inspirujte-se-${item.label}`, {lower: true})"
								:active="inspirationsActiveTab === item.id"
								@click.prevent="handleInspirationsTabSelected(item.id)"
							>
								{{ item.label }}
							</UiTabToggle>
						</li>
					</HorizontalScroll>
				</nav>
				<div class="grid gap-x-4 gap-y-8 overflow-hidden sm:grid-cols-2 lg:grid-cols-3 mb-6 md:mb-12">
					<template v-if="inspirationsActiveTab === 0">
						<ArticlePreviewTile
							v-for="item in allCategoriesItems"
							:key="item.id"
							:article="item"
							class="h-full"
							:link-label="getArticleLinkLabel(item)"
						/>
					</template>

					<template v-if="articlePagination && inspirationsActiveTab === 1">
						<ArticlePreviewTile
							v-for="article in articlePagination.items"
							:key="article.id"
							:article="article"
							class="h-full"
							link-label="Celý článek"
						/>
					</template>

					<template v-if="recipePagination && inspirationsActiveTab === 2">
						<ArticlePreviewTile
							v-for="recipe in recipePagination.items"
							:key="recipe.id"
							:article="recipe"
							class="h-full"
							link-label="Zobrazit recept"
						/>
					</template>

					<template v-if="newsPagination && inspirationsActiveTab === 3">
						<ArticlePreviewTile
							v-for="article in newsPagination.items"
							:key="article.id"
							:article="article"
							class="h-full"
							link-label="Celý článek"
						/>
					</template>
				</div>

				<div v-if="inspirationsActiveTab !== 0" class="w-full flex justify-center">
					<button
						class="btn btn-primary btn-lg max-sm:w-full max-sm:justify-center mx-auto"
						:disabled="!hasMoreInpirations"
						@click="loadMoreInspirations"
					>
						Načist více
						<NuxtIcon
							name="arrow-right-circle"
							class="text-2xl"
							filled
						/>
					</button>
				</div>
			</section>

			<HomepageMyGlobusBanner />

			<RoundedPresentationalTilesSkeleton title="Další zajímavé služby">
				<template #default>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-4.jpg"
								alt="Dárkové karty"
								sizes="xs:260px sm:400px md:260px lg:260px xl:300px xxl:400px inf:500px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'darkove-karty-globus'}})"
								class="before:cover-parent"
							>
								Dárkové karty
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-5.jpg"
								alt="Úvěr bez navýšení"
								sizes="xs:260px sm:400px md:260px lg:260px xl:300px xxl:400px inf:500px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'uver-bez-navyseni'}})"
								class="before:cover-parent"
							>
								Úvěr bez navýšení
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-6.jpg"
								alt="Fotosběrna"
								sizes="xs:260px sm:400px md:260px lg:260px xl:300px xxl:400px inf:500px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'fotosberna'}})"
								class="before:cover-parent"
							>
								Fotosběrna
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-7.jpg"
								alt="Reklamace"
								sizes="xs:260px sm:400px md:260px lg:260px xl:300px xxl:400px inf:500px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'reklamace'}})"
								class="before:cover-parent"
							>
								Reklamace
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
				</template>
				<template #bottom>
					<div class="flex justify-center mt-6 gap-4 md:mt-12">
						<AbsoluteInternalLink
							:to="branchSpecificPath({name: 'sluzby', params: {branch: 'globus'}})"
							class="btn btn-primary btn-lg justify-self-center md:col-start-2"
						>
							Všechny služby
						</AbsoluteInternalLink>
					</div>
				</template>
			</RoundedPresentationalTilesSkeleton>
			<section v-if="blok.productWithdrawal?.length && blok.productWithdrawal[0].products?.length" class="section">
				<div class="container">
					<StoryblokComponent
						v-for="item in blok.productWithdrawal"
						:key="item._uid"
						:blok="item"
					/>
				</div>
			</section>
		</div>
	</main>
</template>

<script setup lang="ts">
	import slugify from 'slugify';
	import {storeToRefs} from 'pinia';
	import type {ISbStoryData} from 'storyblok-js-client';
	import {compact, concat, uniqBy, slice, zip} from 'lodash-es';
	import {useStoryblokHypermarketsStore} from '@/stores/storyblokHypermarkets';
	import {AdPosition} from '@/openapi';
	import {Paginator} from '~/utils/Paginator';
	import {getCommonStoryParams} from '~/storyblok/api/options';
	import {paginationParams} from '~/storyblok/api/utils';
	import useSeoSettings from '~/composables/useSeoSettings';
	import type {
		PageTypeBlogArticleDetailStoryblok,
		PageTypeHomepageStoryblok,
		PageTypeRecipesRecipeDetailStoryblok,
	} from '@/storyblok/types';
	import {GLOBUS_SEO_TITLE} from '#shared/constants';
	import {useStoryblokDatasourcesStore} from '~/stores/storyblokDatasources';

	interface Props {
		blok: PageTypeHomepageStoryblok
	}

	const props = defineProps<Props>();
	const campaignBanner = computed(() => resolvedStory(props.blok.campaignBanner));
	const newsletterBanner = computed(() => resolvedStory(props.blok.newsletterSignupBanner));

	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');
	const branchSpecificPath = useBranchSpecificPath();

	const sorting = 'content.dateOfPublication:desc';
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const informationBar = computed(() => {
		const bar = selectedHypermarketInstance.value?.content.informationBar![0];
		return bar && bar.visibleOnPages?.includes('PageTypeHomepage') ? bar : null;
	});
	const inspirationsActiveTab = ref<number>(0);

	function handleInspirationsTabSelected(id: number) {
		inspirationsActiveTab.value = id;
	}

	const {$storyblokApi} = useNuxtApp();
	const pageSize = 3;

	const datasourcesStore = useStoryblokDatasourcesStore();
	await datasourcesStore.fetchEntries('preparationTime');

	const [
		{pagination: newsPagination, reset: newsPaginationReset, next: newsPaginationNext},
		{pagination: articlePagination, reset: articlePaginationReset, next: articlePaginationNext},
		{pagination: recipePagination, reset: recipePaginationReset, next: recipePaginationNext},
	] = await Promise.all([
		usePagination(
			'homepageNewsPagination',
			() => new Paginator(async (_signal, offset, pageSize) => {
				const {data, total} = await $storyblokApi.get('stories', {
					...getCommonStoryParams(),
					...paginationParams(offset, pageSize),
					is_startpage: false,
					content_type: 'PageTypeBlogArticleDetail',
					starts_with: '_globus-cz/blog/_clanky/aktuality-z-globusu',
					sort_by: sorting,
					filter_query: {
						__or: [
							{hypermarket: {all_in_array: selectedHypermarketInstance.value?.uuid}},
							{hypermarket: {is: 'empty_array'}},
						],
					},
				});

				return {
					items: data.stories as ISbStoryData<PageTypeBlogArticleDetailStoryblok>[],
					total,
				};
			}),
			{
				linear: true,
				pageSize,
				initialPage: 1,
			},
		),
		usePagination(
			'homepageArticlePagination',
			() => new Paginator(async (_signal, offset, pageSize) => {
				const {data, total} = await $storyblokApi.get('stories', {
					...getCommonStoryParams(),
					...paginationParams(offset, pageSize),
					is_startpage: false,
					content_type: 'PageTypeBlogArticleDetail',
					sort_by: sorting,
					filter_query: {
						__or: [
							{hypermarket: {all_in_array: selectedHypermarketInstance.value?.uuid}},
							{hypermarket: {is: 'empty_array'}},
						],
					},
				});

				return {
					items: data.stories as ISbStoryData<PageTypeBlogArticleDetailStoryblok>[],
					total,
				};
			}),
			{
				linear: true,
				pageSize,
				initialPage: 1,
			},
		),
		usePagination(
			'homepageRecipePagination',
			() => new Paginator(async (_signal, offset, pageSize) => {
				const {data, total} = await $storyblokApi.get('stories', {
					...getCommonStoryParams(),
					...paginationParams(offset, pageSize),
					is_startpage: false,
					content_type: 'PageTypeRecipesRecipeDetail',
					sort_by: sorting,
					filter_query: {
						__or: [
							{hypermarket: {all_in_array: selectedHypermarketInstance.value?.uuid}},
							{hypermarket: {is: 'empty_array'}},
						],
					},
				});

				return {
					items: data.stories as ISbStoryData<PageTypeRecipesRecipeDetailStoryblok>[],
					total,
				};
			}),
			{
				linear: true,
				pageSize,
				initialPage: 1,
			},
		),
	]);

	const allCategoriesItems = computed(() => {
		const zipped = zip(
			articlePagination.value?.items ?? [],
			recipePagination.value?.items ?? [],
			newsPagination.value?.items ?? [],
		);

		const uniqueItems = uniqBy(compact(concat(...zipped)), 'id');

		return slice(
			uniqueItems,
			0,
			pageSize,
		);
	});

	const inspirationsTabs = computed<{id: number, label: string}[]>(() => compact([
		allCategoriesItems.value?.length && {
			id: 0,
			label: 'Vše',
		},
		articlePagination.value?.totalItems && {
			id: 1,
			label: 'Články',
		},
		recipePagination.value?.totalItems && {
			id: 2,
			label: 'Recepty',
		},
		newsPagination.value?.totalItems && {
			id: 3,
			label: 'Novinky',
		},
	]));

	const hasMoreInpirations = computed(() => {
		if (inspirationsActiveTab.value === 1) {
			return articlePagination.value?.hasMore;
		}

		if (inspirationsActiveTab.value === 2) {
			return recipePagination.value?.hasMore;
		}

		if (inspirationsActiveTab.value === 3) {
			return newsPagination.value?.hasMore;
		}

		return false;
	});

	function loadMoreInspirations() {
		if (inspirationsActiveTab.value === 1) {
			articlePaginationNext();
		}

		if (inspirationsActiveTab.value === 2) {
			recipePaginationNext();
		}

		if (inspirationsActiveTab.value === 3) {
			newsPaginationNext();
		}
	}

	function getArticleLinkLabel(article: {content: {component?: string}}) {
		switch (article.content.component) {
			case 'PageTypeBlogArticleDetail':
				return 'Celý článek';
			case 'PageTypeRecipesRecipeDetail':
				return 'Zobrazit recept';
			default:
				return 'Zobrazit';
		}
	}

	watch(selectedHypermarketInstance, () => {
		articlePaginationReset();
		newsPaginationReset();
		recipePaginationReset();
	});

	const svg = await import('@/assets/icons/custom/globus.svg?url');

	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		url: useCurrentUrl(),
		logo: `${useBaseUrl()}${svg.default}`,
	};

	useHead({
		script: [
			{
				type: 'application/ld+json',
				textContent: JSON.stringify(jsonLd),
			},
		],
	});

	useSeoSettings(props.blok.seoSettings?.[0]);
</script>
